import React, { useMemo, useState, useRef } from 'react';
import { Card } from '../components/ui/Card';
import { useProjects } from '../hooks/useProjects';
import { useCompanies } from '../hooks/useCompanies';
import { useExpenses } from '../hooks/useExpenses';
import { formatCurrency, MainCostCategory, COST_CATEGORIES } from '../constants';
import type { CostCategoryDetail } from '../types';
import { StatCard } from '../components/ui/StatCard';
import { Button } from '../components/ui/Button';
import { PrintableDashboard } from '../components/PrintableDashboard';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

// Icons
import { BriefcaseIcon } from '../components/icons/BriefcaseIcon';
import { DocumentTextIcon } from '../components/icons/DocumentTextIcon';
import { CalendarIcon } from '../components/icons/CalendarIcon';
import { CurrencyDollarIcon } from '../components/icons/CurrencyDollarIcon';
import { ArrowUpRightIcon } from '../components/icons/ArrowUpRightIcon';
import { ArrowDownRightIcon } from '../components/icons/ArrowDownRightIcon';
import { ChatBubbleLeftEllipsisIcon } from '../components/icons/ChatBubbleLeftEllipsisIcon';
import { ListBulletIcon } from '../components/icons/ListBulletIcon';
import { MapPinIcon } from '../components/icons/MapPinIcon';
import { PrinterIcon } from '../components/icons/PrinterIcon';


// Component for the loading state skeleton screen
const DashboardLoadingSkeleton: React.FC = () => {
  const SkeletonBar = ({ className = '' }: { className?: string }) => (
    <div className={`bg-gray-200 dark:bg-dark-tertiary rounded animate-pulse ${className}`} />
  );

  return (
    <div className='space-y-6'>
      <div className="flex justify-between items-center">
        <SkeletonBar className="h-9 w-1/3" />
        <SkeletonBar className="h-10 w-48" />
      </div>
      
      {/* Stat Cards Skeletons */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="h-[128px]">
            <div className="flex flex-col items-center justify-center text-center h-full space-y-2">
              <SkeletonBar className="h-12 w-12 rounded-full" />
              <SkeletonBar className="h-4 w-24" />
              <SkeletonBar className="h-6 w-32" />
            </div>
          </Card>
        ))}
      </div>
      
      {/* Launch Count Skeletons */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SkeletonBar className="h-14 w-full" />
        <SkeletonBar className="h-14 w-full" />
      </div>

      {/* Charts & Lists Skeletons */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card title={<SkeletonBar className="h-6 w-32" />} className="lg:col-span-1 h-[344px]">
          <div className="flex items-end justify-center h-full p-4 space-x-4">
             <SkeletonBar className="w-1/5 h-3/4" />
             <SkeletonBar className="w-1/5 h-1/2" />
             <SkeletonBar className="w-1/5 h-2/3" />
             <SkeletonBar className="w-1/5 h-1/4" />
             <SkeletonBar className="w-1/5 h-5/6" />
          </div>
        </Card>
        <Card title={<SkeletonBar className="h-6 w-32" />} className="lg:col-span-1 h-[344px]">
           <div className="flex items-center justify-center h-full">
                <SkeletonBar className="h-48 w-full" />
           </div>
        </Card>
        <Card title={<SkeletonBar className="h-6 w-32" />} className="lg:col-span-1 h-[344px]">
          <div className="space-y-4 p-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex justify-between items-center">
                <div className="space-y-1"><SkeletonBar className="h-5 w-20" /><SkeletonBar className="h-3 w-12" /></div>
                <div className="space-y-1 text-right"><SkeletonBar className="h-5 w-24" /><SkeletonBar className="h-3 w-16" /></div>
              </div>
            ))}
          </div>
        </Card>
      </div>
      
      {/* Region Card Skeleton */}
      <Card title={<SkeletonBar className="h-6 w-32" />}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
                <div key={i} className="p-4 rounded-lg flex items-start bg-gray-100 dark:bg-dark-surface">
                    <SkeletonBar className="h-12 w-12 rounded-full mr-4 flex-shrink-0" />
                    <div className="flex-grow space-y-2">
                        <SkeletonBar className="h-5 w-16" />
                        <SkeletonBar className="h-5 w-24" />
                        <SkeletonBar className="h-3 w-20" />
                    </div>
                </div>
            ))}
        </div>
      </Card>
    </div>
  );
};


// Simple Bar Chart Component
const BarChart: React.FC<{ data: { label: string, value: number, color: string, percentage: number }[] }> = ({ data }) => {
    const maxValue = Math.max(...data.map(d => d.value), 1); // Use 1 to avoid division by zero

    return (
        <div className="h-64 flex items-end space-x-2 sm:space-x-4 p-4">
            {data.map((item, index) => (
                <div key={index} className="flex-1 flex flex-col items-center justify-end h-full">
                    <div className="text-xs font-semibold text-gray-800 dark:text-dark-text-primary mb-1">
                        {item.percentage > 0.1 ? `${item.percentage.toFixed(1)}%` : ''}
                    </div>
                    <div
                        className="w-full rounded-t-md transition-all duration-300 hover:opacity-80"
                        style={{ 
                            height: `${(item.value / maxValue) * 80}%`, // Use 80% of height to leave space for labels
                            backgroundColor: item.color 
                        }}
                        title={`${item.label}: ${formatCurrency(item.value)} (${item.percentage.toFixed(1)}%)`}
                    />
                    <div className="text-xs text-center text-gray-600 dark:text-dark-text-secondary mt-2 w-full">{item.label}</div>
                </div>
            ))}
        </div>
    );
};

// Simple Line Chart Component for multiple series
const LineChart: React.FC<{ data: { color: string, points: { label: string, value: number }[] }[]; currentMonthIndex: number; }> = ({ data, currentMonthIndex }) => {
    const [tooltip, setTooltip] = useState<{ x: number, y: number, label: string, value: number } | null>(null);

    if (!data || data.length === 0 || data.every(series => series.points.length === 0)) {
        return <div className="flex items-center justify-center h-full text-gray-500 dark:text-dark-text-secondary">Dados insuficientes para exibir evolução.</div>;
    }
    
    const allValues = data.flatMap(series => series.points.map(p => p.value));
    const maxValue = Math.max(...allValues, 1);
    
    const labels = data[0]?.points.map(p => p.label) || [];

    const chartHeight = 100;
    const chartWidth = 100;
    const yPadding = 5;
    const chartAreaHeight = chartHeight - (yPadding * 2);

    const getCoords = (point: { value: number }, i: number, length: number) => {
        const x = length > 1 ? (i / (length - 1)) * chartWidth : chartWidth / 2;
        const y = chartHeight - ((point.value / maxValue) * chartAreaHeight) - yPadding;
        return { x, y };
    };

    return (
        <div className="h-64 p-4 flex flex-col relative">
            {tooltip && (
                <div
                    className="absolute bg-gray-800 dark:bg-dark-primary text-white text-xs rounded py-1 px-2 pointer-events-none transition-all z-10"
                    style={{
                        left: `${tooltip.x}%`,
                        top: `${tooltip.y}%`, // Position at the circle's Y
                        transform: 'translate(-50%, -150%)', // Move it up and center it
                    }}
                >
                    {tooltip.label.replace('\n', ' ')}: {formatCurrency(tooltip.value)}
                </div>
            )}
            <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="w-full h-full" preserveAspectRatio="none">
                {/* Vertical Line for current month */}
                {currentMonthIndex >= 0 && data[0]?.points.length > 1 && (() => {
                    const { x } = getCoords({ value: 0 }, currentMonthIndex, data[0].points.length);
                    return (
                        <line
                            x1={x} y1={yPadding}
                            x2={x} y2={chartHeight - yPadding}
                            stroke="rgba(156, 163, 175, 0.4)" // gray-400 with opacity
                            strokeWidth="0.5"
                            strokeDasharray="2 2"
                        />
                    );
                })()}

                {data.map(series => {
                    if (series.points.length === 0) return null;

                    const pointsStr = series.points.map((p, i) => {
                        const { x, y } = getCoords(p, i, series.points.length);
                        return `${x},${y}`;
                    }).join(' ');

                    return (
                        <g key={series.color}>
                            {series.points.length > 1 && (
                                <polyline fill="none" stroke={series.color} strokeWidth="1" points={pointsStr} />
                            )}
                            
                            {series.points.map((point, i) => {
                                const { x, y } = getCoords(point, i, series.points.length);
                                
                                return (
                                    <circle
                                        key={`${point.label}-${i}`}
                                        cx={x}
                                        cy={y}
                                        r="2.5"
                                        fill={series.color}
                                        stroke="#ffffff" // Use light card background color
                                        strokeWidth="1.5"
                                        onMouseOver={() => setTooltip({ x, y, label: point.label, value: point.value })}
                                        onMouseOut={() => setTooltip(null)}
                                        className="dark:stroke-[#2d3748] cursor-pointer"
                                    />
                                );
                            })}
                        </g>
                    );
                })}
            </svg>
             <div className="flex justify-between text-xs text-gray-600 dark:text-dark-text-secondary mt-1 border-t border-gray-200 dark:border-dark-tertiary pt-2">
                {labels.map(label => (
                    <div key={label} className="text-center leading-tight">
                        {label.split('\n').map((part, index) => <div key={index}>{part}</div>)}
                    </div>
                ))}
            </div>
        </div>
    );
};

const getCategoryShortLabel = (category: CostCategoryDetail): string => {
    switch(category.id) {
        case MainCostCategory.DESPESAS_ADM: return 'Desp Adm';
        case MainCostCategory.TRIBUTOS_INSS: return 'INSS';
        default: return category.name.split(' - ')[0]; // 'CDO', 'CIO', 'Tributos' etc.
    }
};


export const DashboardPage: React.FC = () => {
    const { projects, loading: projectsLoading } = useProjects();
    const { companies, loading: companiesLoading } = useCompanies();
    const { allExpenses, loading: expensesLoading } = useExpenses();

    const [isGenerating, setIsGenerating] = useState(false);
    const printableRef = useRef<HTMLDivElement>(null);
    const [evolutionFilter, setEvolutionFilter] = useState<'default' | string>('default');
    
    const isLoading = projectsLoading || companiesLoading || expensesLoading;

    const stats = useMemo(() => {
        const now = new Date();
        const todayStr = now.toISOString().split('T')[0];
        const currentMonthStr = todayStr.substring(0, 7);
        const prevMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const prevMonthStr = prevMonthDate.toISOString().substring(0, 7);
        const currentMonthName = now.toLocaleString('pt-BR', { month: 'long' });

        const expensesThisMonth = allExpenses.filter(e => e.month === currentMonthStr);
        const expensesToday = allExpenses.filter(e => e.date === todayStr);

        return {
            totalObras: projects.length,
            custoTotalGeral: allExpenses.reduce((sum, e) => sum + e.amount, 0),
            custoMesAtual: expensesThisMonth.reduce((sum, e) => sum + e.amount, 0),
            custoMesAnterior: allExpenses.filter(e => e.month === prevMonthStr).reduce((sum, e) => sum + e.amount, 0),
            lancamentosHoje: expensesToday.reduce((sum, e) => sum + e.amount, 0),
            totalLancamentos: allExpenses.length,
            lancamentosMes: expensesThisMonth.length,
            currentMonthName: currentMonthName.charAt(0).toUpperCase() + currentMonthName.slice(1)
        };
    }, [allExpenses, projects.length]);

    const getAdaptiveCurrencyClass = (value: number) => {
        const valueStr = formatCurrency(value);
        if (valueStr.length > 18) { // e.g. "R$ 100.000.000,00"
            return 'text-lg font-bold';
        }
        if (valueStr.length > 13) { // e.g. "R$ 1.000.000,00"
            return 'text-xl font-bold';
        }
        return 'text-2xl font-bold';
    };

    const costByCategoryData = useMemo(() => {
        const totalCost = stats.custoTotalGeral;
        if (!allExpenses.length || totalCost === 0) return [];

        const categoryColors: { [key in MainCostCategory]: string } = {
            [MainCostCategory.CDO]: '#36A2EB',
            [MainCostCategory.CIO]: '#FF9F40',
            [MainCostCategory.DESPESAS_ADM]: '#4BC0C0',
            [MainCostCategory.TRIBUTOS]: '#FF6384',
            [MainCostCategory.TRIBUTOS_INSS]: '#9966FF',
        };

        const costsByMainCategory = allExpenses.reduce((acc, expense) => {
            acc[expense.mainCategory] = (acc[expense.mainCategory] || 0) + expense.amount;
            return acc;
        }, {} as { [key in MainCostCategory]?: number });

        return COST_CATEGORIES.map(category => {
            const value = costsByMainCategory[category.id] || 0;
            const percentage = totalCost > 0 ? (value / totalCost) * 100 : 0;
            return {
                label: getCategoryShortLabel(category),
                value: value,
                color: categoryColors[category.id] || '#CCCCCC',
                percentage: percentage,
            };
        });
    }, [stats.custoTotalGeral, allExpenses]);

    const costEvolutionData = useMemo(() => {
        if (!allExpenses.length) return [];
        
        const monthlyCosts: { [month: string]: number } = {};
        const now = new Date();
        // Initialize last 12 months with 0
        for (let i = 11; i >= 0; i--) {
            const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const monthKey = date.toISOString().substring(0, 7);
            monthlyCosts[monthKey] = 0;
        }

        // Sum expenses into the monthly buckets
        allExpenses.forEach(expense => {
            if (expense.month && monthlyCosts[expense.month] !== undefined) {
                monthlyCosts[expense.month] += expense.amount;
            }
        });

        const points = Object.entries(monthlyCosts).map(([month, total]) => {
            const [year, monthNum] = month.split('-');
            const date = new Date(parseInt(year), parseInt(monthNum) - 1, 15);
            const shortMonth = date.toLocaleString('pt-BR', { month: 'short' }).replace('.', '');
            const shortYear = date.getFullYear().toString().slice(-2);
            return {
                label: `${shortMonth}\n${shortYear}`,
                value: total,
            };
        });

        return [{
            name: 'Custo Total',
            color: '#36A2EB',
            points: points,
        }];
    }, [allExpenses]);


    const latestExpenses = useMemo(() => {
        const projectMap = new Map(projects.map(p => [p.id, p.name]));
        return allExpenses
            // @ts-ignore
            .sort((a, b) => new Date(b.createdAt || b.date).getTime() - new Date(a.createdAt || a.date).getTime())
            .slice(0, 5)
            .map(exp => ({
                ...exp,
                projectName: projectMap.get(exp.projectId) || 'Obra desconhecida'
            }));
    }, [allExpenses, projects]);
    
    const obrasPorRegiao = useMemo(() => {
        // Calculate total cost for each project first.
        const costsByProject = allExpenses.reduce((acc, expense) => {
            acc[expense.projectId] = (acc[expense.projectId] || 0) + expense.amount;
            return acc;
        }, {} as { [key: string]: number });
        
        const dataByState: { [state: string]: { count: number; totalCost: number } } = {};
        const companiesMap = new Map(companies.map(c => [c.id, c]));

        projects.forEach(p => {
            const company = companiesMap.get(p.companyId);
            if (company?.address?.state) {
                const state = company.address.state.trim().toUpperCase();
                if (state.length === 2) {
                    if (!dataByState[state]) {
                        dataByState[state] = { count: 0, totalCost: 0 };
                    }
                    dataByState[state].count += 1;
                    // FIX: Use the calculated cost from the map instead of a non-existent property
                    dataByState[state].totalCost += (costsByProject[p.id] || 0);
                }
            }
        });
        
        const totalGeral = stats.custoTotalGeral;

        return Object.entries(dataByState)
            .map(([state, data]) => ({ 
                state, 
                ...data,
                percentage: totalGeral > 0 ? (data.totalCost / totalGeral) * 100 : 0
            }))
            .sort((a, b) => b.totalCost - a.totalCost);
    }, [projects, companies, allExpenses, stats.custoTotalGeral]);


    const isCostTrendUp = stats.custoMesAtual > stats.custoMesAnterior;
    const TrendIcon = isCostTrendUp ? ArrowUpRightIcon : ArrowDownRightIcon;
    const trendColor = isCostTrendUp ? 'text-red-500' : 'text-green-500';
    
    const currentMonthDataIndex = useMemo(() => {
        return costEvolutionData[0]?.points.length - 1 || -1;
    }, [costEvolutionData]);

    const handleGeneratePdf = async () => {
        const reportElement = printableRef.current;
        if (!reportElement || isGenerating) return;

        setIsGenerating(true);
        try {
            const canvas = await html2canvas(reportElement, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff'
            });

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const imgWidth = canvas.width;
            const imgHeight = canvas.height;
            const ratio = imgWidth / imgHeight;
            const pdfImageWidth = pdfWidth;
            const pdfImageHeight = pdfImageWidth / ratio;
            
            let heightLeft = pdfImageHeight;
            let position = 0;
            
            pdf.addImage(imgData, 'PNG', 0, position, pdfImageWidth, pdfImageHeight);
            heightLeft -= pdfHeight;

            while (heightLeft > 0) {
                position -= pdfHeight;
                pdf.addPage();
                pdf.addImage(imgData, 'PNG', 0, position, pdfImageWidth, pdfImageHeight);
                heightLeft -= pdfHeight;
            }

            const today = new Date().toISOString().slice(0, 10);
            pdf.save(`Relatorio_Dashboard_${today}.pdf`);

        } catch (error) {
            console.error("Erro ao gerar PDF:", error);
            alert("Ocorreu um erro ao tentar gerar o PDF.");
        } finally {
            setIsGenerating(false);
        }
    };
    
    if (isLoading) {
        return <DashboardLoadingSkeleton />;
    }


    return (
        <div className='space-y-6'>
            <div className="flex justify-between items-center">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-dark-text-primary">Custos de Obras</h1>
                <Button 
                    onClick={handleGeneratePdf} 
                    isLoading={isGenerating}
                    leftIcon={<PrinterIcon className="text-xl"/>}
                    disabled={isGenerating}
                >
                    {isGenerating ? 'Gerando...' : 'Gerar Relatório'}
                </Button>
            </div>
            
            {/* Stat Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard icon={<BriefcaseIcon className="text-4xl" />} title="Total de Obras" value={stats.totalObras} valueClassName="text-4xl font-bold" />
                <StatCard icon={<DocumentTextIcon className="text-4xl" />} title={`Custo Total (${stats.currentMonthName})`}>
                    <div className='flex items-center space-x-2'>
                        <span className={getAdaptiveCurrencyClass(stats.custoMesAtual)}>{formatCurrency(stats.custoMesAtual)}</span>
                        {stats.custoMesAnterior > 0 && (
                            <TrendIcon className={`text-xl ${trendColor}`} />
                        )}
                    </div>
                </StatCard>
                <StatCard icon={<CalendarIcon className="text-4xl" />} title="Lançamentos de hoje" value={formatCurrency(stats.lancamentosHoje)} valueClassName={getAdaptiveCurrencyClass(stats.lancamentosHoje)} />
                <StatCard icon={<CurrencyDollarIcon className="text-4xl" />} title="Custo Total Geral" value={formatCurrency(stats.custoTotalGeral)} valueClassName={getAdaptiveCurrencyClass(stats.custoTotalGeral)} />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className='bg-white dark:bg-dark-secondary p-4 rounded-lg flex items-center'>
                    <ChatBubbleLeftEllipsisIcon className='text-2xl mr-4 text-green-400'/>
                    <span className='text-gray-900 dark:text-dark-text-primary font-semibold'>{stats.totalLancamentos}</span>
                    <span className='text-gray-600 dark:text-dark-text-secondary ml-2'>Total de lançamentos</span>
                </div>
                <div className='bg-white dark:bg-dark-secondary p-4 rounded-lg flex items-center'>
                    <ListBulletIcon className='text-2xl mr-4 text-green-400'/>
                    <span className='text-gray-900 dark:text-dark-text-primary font-semibold'>{stats.lancamentosMes}</span>
                    <span className='text-gray-600 dark:text-dark-text-secondary ml-2'>Total de lançamentos ({stats.currentMonthName})</span>
                </div>
            </div>

            {/* Charts & Lists */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card title="Custos por Categoria" className="lg:col-span-1">
                    <BarChart data={costByCategoryData} />
                </Card>
                <Card 
                    title="Evolução de Custos" 
                    className="lg:col-span-1"
                >
                    <LineChart data={costEvolutionData} currentMonthIndex={currentMonthDataIndex} />
                </Card>
                <Card title="Últimos Lançamentos" className="lg:col-span-1">
                    <div className="space-y-4">
                        {latestExpenses.length > 0 ? latestExpenses.map((exp: any) => (
                            <div key={exp.id} className="flex items-center justify-between p-2 hover:bg-gray-100 dark:hover:bg-dark-tertiary rounded-md">
                                <div className='flex-shrink-0'>
                                    <p className="text-md font-semibold text-gray-900 dark:text-dark-text-primary">{formatCurrency(exp.amount)}</p>
                                    <p className="text-xs text-gray-600 dark:text-dark-text-secondary">{exp.subCategoryItemName.substring(0, 20)}</p>
                                </div>
                                <div className="text-right ml-2">
                                    <p className="text-sm font-medium text-gray-900 dark:text-dark-text-primary truncate" title={exp.projectName}>{exp.projectName.split(' - ')[0]}</p>
                                    <p className="text-xs text-gray-600 dark:text-dark-text-secondary">{exp.projectName.split(' - ')[1]}</p>
                                </div>
                            </div>
                        )) : <p className="text-gray-500 dark:text-dark-text-secondary text-center py-4">Nenhum lançamento recente.</p>}
                    </div>
                </Card>
            </div>
            
            {/* Obras por Região Card */}
            <Card title="Obras por Região">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {obrasPorRegiao.length > 0 ? obrasPorRegiao.map((item) => (
                        <div key={item.state} className="p-4 rounded-lg flex items-start transition-all duration-300 bg-gray-100 dark:bg-dark-surface hover:shadow-lg">
                            <div className="flex-shrink-0 mr-4 p-3 rounded-full bg-brand-teal/20 text-brand-teal">
                                <MapPinIcon className="text-3xl" />
                            </div>
                            <div className="flex-grow">
                                <div className="flex justify-between items-baseline">
                                    <span className="text-xl font-bold text-gray-900 dark:text-dark-text-primary">{item.state}</span>
                                    <span className="text-sm text-gray-600 dark:text-dark-text-secondary">{item.count} {item.count > 1 ? 'obras' : 'obra'}</span>
                                </div>
                                <p className="text-lg font-semibold text-brand-lime mt-1">{formatCurrency(item.totalCost)}</p>
                                <p className="text-xs text-gray-500 dark:text-dark-text-secondary">{item.percentage.toFixed(2)}% do total</p>
                            </div>
                        </div>
                    )) : (
                        <p className="text-gray-500 dark:text-dark-text-secondary text-center py-4 col-span-full">Nenhuma obra com localização definida.</p>
                    )}
                </div>
            </Card>

            {/* Hidden component for printing */}
            <div className="absolute left-[-9999px] top-0 print:relative print:left-0">
                <div ref={printableRef}>
                    <PrintableDashboard
                        stats={stats}
                        costByCategoryData={costByCategoryData}
                        costEvolutionData={costEvolutionData}
                        latestExpenses={latestExpenses}
                        obrasPorRegiao={obrasPorRegiao}
                        currentMonthDataIndex={currentMonthDataIndex}
                        evolutionFilterLabel={"Últimos 12 meses"}
                    />
                </div>
            </div>
        </div>
    );
};
